import { EditOutlined } from '@ant-design/icons';
import { Button, DatePicker } from 'antd';
import { useAppDispatch } from 'app/store';
import { uiModel } from 'entities/ui';
import { ImportPreviewTableRow } from 'entities/ui/model/absence';
import { PICKER_DATE_MASK, SERVER_DATE_MASK } from 'shared/constants';
import { convertDate } from 'shared/helpers/date';
import { memo, useRef, useState } from 'react';
import useClickOutside from 'shared/hooks/use-click-outside';
import { useDuplicateErrorRow, useIntervalErrorRow } from 'features/absence/absence-import/hooks';
import dayjs, { Dayjs } from 'dayjs';
import style from './index.module.scss';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  rowId: number;
  record: ImportPreviewTableRow;
  inputType: string;
  editing: boolean;
  title: string;
  dataIndex: string;
  index: number;
  children: React.ReactNode;
  startedAt?: Dayjs;
  endedAt?: Dayjs;
}

export const EditableCell: React.FC<EditableCellProps> = memo(
  ({ rowId, record, editing, children, dataIndex, inputType, startedAt, endedAt, ...restProps }) => {
    const dispatch = useAppDispatch();
    const divRef = useRef<HTMLDivElement>(null);

    const [edit, setEdit] = useState<boolean>(false);

    const disabledStartDate = (startValue: Dayjs) => {
      if (!startValue || !endedAt) {
        return false;
      }
      return startValue.valueOf() > dayjs(endedAt, PICKER_DATE_MASK).valueOf();
    };

    const disabledEndDate = (endValue: Dayjs) => {
      if (!endValue || !startedAt) {
        return false;
      }
      return endValue.valueOf() <= dayjs(startedAt, PICKER_DATE_MASK).valueOf();
    };

    const onClick = () => {
      setEdit(true);
    };

    const duplicateRow = useDuplicateErrorRow(rowId);
    const intervalErrorRow = useIntervalErrorRow(rowId);

    const onChange = (value: Dayjs | null) => {
      // Для дубликата и строки с ошибкой пересечения дат, обновляем поля ошибки
      const duplicatedRows = duplicateRow
        ? [
            {
              id: duplicateRow.tableId,
              changes: { errors: duplicateRow.errors ? { ...duplicateRow.errors, duplicate: null } : undefined },
            },
          ]
        : [];
      const intervalRows = intervalErrorRow
        ? [
            {
              id: intervalErrorRow.tableId,
              changes: { errors: intervalErrorRow.errors ? { ...intervalErrorRow.errors, interval: null } : undefined },
            },
          ]
        : [];

      dispatch(
        uiModel.Absence.actions.updateRows([
          {
            id: rowId,
            changes: {
              [dataIndex]: value,
              errors: record.errors ? { ...record.errors, date: null, duplicate: null, interval: null } : undefined,
            },
          },
          ...duplicatedRows,
          ...intervalRows,
        ]),
      );
    };

    const closeCalendar = () => setEdit(false);

    useClickOutside(divRef, closeCalendar);

    return (
      <td {...restProps}>
        {editing ? (
          <div ref={divRef}>
            {edit ? (
              <DatePicker
                bordered={false}
                format={PICKER_DATE_MASK}
                placeholder="ДД.ММ.ГГГГ"
                disabledDate={dataIndex === 'startedAt' ? disabledStartDate : disabledEndDate}
                allowClear={false}
                style={{ cursor: 'pointer' }}
                value={
                  { startedAt, endedAt }[dataIndex]
                    ? dayjs(
                        { startedAt: dayjs(startedAt, PICKER_DATE_MASK), endedAt: dayjs(endedAt, PICKER_DATE_MASK) }[
                          dataIndex
                        ],
                      )
                    : undefined
                }
                onChange={onChange}
                open={true}
              />
            ) : (
              <div className={style.editable}>
                {{ startedAt, endedAt }[dataIndex] ? (
                  <span className={record.errors?.date ? style.error : ''}>
                    {dayjs({ startedAt, endedAt }[dataIndex], PICKER_DATE_MASK).format(PICKER_DATE_MASK)}
                  </span>
                ) : (
                  <span className={!record.startedAt || !record.endedAt ? style.error : ''}>ДД.ММ.ГГГГ</span>
                )}
                <Button icon={<EditOutlined />} type="link" onClick={onClick} />
              </div>
            )}
          </div>
        ) : (
          children
        )}
      </td>
    );
  },
);

export default EditableCell;
